import { useState } from "react";

import {
  Box,
  Grid,
  Input,
  Tag,
  Button,
  Text,
  Avatar,
  EmptyState,
  FlexiPagination,
  DropDown,
} from "@flexisaf/flexibull2";

import styled from "styled-components";

import useQuestionsGet from "../../../hooks/questions/useQuestionsGet";
import useQuestionEditor from "../../../hooks/questions/useQuestionEditor";
import DeleteModal from "../../../components/DeleteModal/DeleteModal";

import SectionLoader from "../../../components/custom/sectionLoader";
import QuestionEditor from "../../../components/questions/modals/questionEditor";
import Theme from "../../../theme";
import BulkQuestionUpload from "../../../components/questions/modals/bulkQuestionUpload";

import SingleIcon from "../../../assets/file.svg";
import MultipleIcon from "../../../assets/multiple-files.svg";
import AIBotIcon from "../../../assets/bot.svg";
import GptEditor from "../../../components/questions/modals/gptEditor";
import ContentRenderer from "../../../components/questions/ContentRenderer";

const Questions = ({ paperId, paper }) => {
  const [modal, setModal] = useState({
    show: false,
    data: null,
  });

  const [openBulkModal, setOpenBulkModal] = useState(false);

  const [openGptModal, setOpenGptModal] = useState(false);

  const [deleteQuestionModal, setDeleteQuestionModal] = useState({
    openModal: false,
    data: null,
  });

  const {
    loadingQuestions,
    questions,
    searchText,
    setSearchText,
    refetch,
    pageOptions,
    totalElements,
    limit,
    setLimit,
    setPage,
    page,
  } = useQuestionsGet({
    paperId,
  });
  const { deleteQuestion, isDeleting } = useQuestionEditor();

  const handleDelete = (id) => {
    deleteQuestion({
      id,
      callback: () => {
        refetch();
      },
    });
  };

  if (loadingQuestions && searchText === "")
    return <SectionLoader color={Theme.PrimaryColor} height="60vh" />;

  return (
    <Box>
      {searchText === "" && !questions?.items?.length ? (
        <>
          <EmptyState
            title="No questions created yet"
            info="Please click any of the buttons below to start crafting your questions"
            action={
              <Grid default="repeat(3, 1fr)" md="1fr" gap="20px">
                <ActionCard>
                  <img src={SingleIcon} alt="create question" />
                  <Text>Single Question</Text>
                  <Button
                    pale
                    onClick={() =>
                      setModal({
                        show: true,
                        data: null,
                      })
                    }
                  >
                    Create
                  </Button>
                </ActionCard>
                <ActionCard>
                  <img src={MultipleIcon} alt="bulk upload question" />
                  <Text> Bulk upload</Text>
                  <Button pale onClick={() => setOpenBulkModal(!openBulkModal)}>
                    {" "}
                    Upload
                  </Button>
                </ActionCard>
                <ActionCard>
                  <img src={AIBotIcon} alt="generate question" />
                  <Text>AI powered</Text>
                  <Button pale onClick={() => setOpenGptModal(!openGptModal)}>
                    Generate
                  </Button>
                </ActionCard>
              </Grid>
            }
          />
        </>
      ) : (
        <>
          <Grid default="max-content auto max-content max-content">
            <Input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search by question name"
              width="340px"
            />
            <Box style={{ marginTop: "8px" }}>
              <Tag round spaceRight>
                {`${questions?.count || 0} questions`}
              </Tag>
            </Box>
            <DropDown
              label={
                <Button pale iconRight={<i className="flexibull-angle-down" />}>
                  Add Question
                </Button>
              }
              menuAlign="bottom left"
            >
              <li
                onClick={() =>
                  setModal({
                    show: true,
                    data: null,
                  })
                }
              >
                Create
              </li>
              <li onClick={() => setOpenBulkModal(!openBulkModal)}>Upload</li>
              <li onClick={() => setOpenGptModal(!openGptModal)}>Generate</li>
            </DropDown>
          </Grid>

          <Box margin="30px 0">
            {questions?.items?.length ? (
              <>
                <Grid default=" repeat( auto-fit, minmax(350px, 1fr) )">
                  {questions.items.map((question) => {
                    return (
                      <QuestionCell onClick={() => {}} key={question.id}>
                        <Box>
                          <CardTop>
                            <Text color={Theme.PrimaryGrey}>
                              <i className="saf-archive-1" /> {question?.topic}
                            </Text>
                            <DropDown
                              label={
                                <Text
                                  bold
                                  size="14px"
                                  style={{ cursor: "pointer" }}
                                >
                                  ...
                                </Text>
                              }
                              menuAlign="bottom right"
                              trigger="click"
                              menuList={[
                                {
                                  onClick: () =>
                                    setModal({
                                      show: true,
                                      data: question,
                                    }),
                                  label: "Edit Question",
                                  iconLeft: "saf-receipt-edit",
                                },
                                {
                                  onClick: () => {
                                    setDeleteQuestionModal({
                                      openModal: true,
                                      data: question?.id,
                                    });
                                  },
                                  label: "Delete Question",
                                  color: Theme.PrimaryRed,
                                  iconLeft: "saf-trash",
                                  process: isDeleting,
                                },
                              ]}
                            />
                          </CardTop>
                        </Box>
                        <Box style={{ flexGrow: 2 }}>
                          <Box
                            margin="20px 0"
                            style={{
                              maxHeight: "12rem",
                              minHeight: "8rem",
                              overflowY: "scroll",
                              scrollbarWidth: "none",
                            }}
                          >
                            <ContentRenderer content={question?.text} />
                          </Box>
                        </Box>
                        <CardBottom>
                          <Box>
                            <Box
                              display="flex"
                              style={{ alignItems: "center" }}
                            >
                              <Avatar
                                name={`SAFlearn Admin`}
                                margin="0 10px 0 0"
                                size="30px"
                                round
                              />
                              <Text>SAFlearn admin</Text>
                            </Box>
                          </Box>
                        </CardBottom>
                      </QuestionCell>
                    );
                  })}
                </Grid>
                <FlexiPagination
                  pageCounts={pageOptions}
                  itemsDisplayed
                  total={totalElements}
                  pageSize={limit}
                  onChange={(page) => {
                    setPage(page);
                  }}
                  changePageSize={({ value }) => {
                    setPage(1);
                    setLimit(value);
                  }}
                  current={page}
                />
              </>
            ) : (
              <EmptyState
                title="No questions created yet"
                info="Please click any of the buttons below to start crafting your questions"
                action={
                  <Grid default="repeat(3, 1fr)" md="1fr" gap="20px">
                    <ActionCard>
                      <img src={SingleIcon} alt="create question" />
                      <Text>Single Question</Text>
                      <Button
                        pale
                        onClick={() =>
                          setModal({
                            show: true,
                            data: null,
                          })
                        }
                      >
                        Create
                      </Button>
                    </ActionCard>
                    <ActionCard>
                      <img src={MultipleIcon} alt="bulk upload question" />
                      <Text> Bulk upload</Text>
                      <Button
                        pale
                        onClick={() => setOpenBulkModal(!openBulkModal)}
                      >
                        {" "}
                        Upload
                      </Button>
                    </ActionCard>
                    <ActionCard>
                      <img src={AIBotIcon} alt="generate question" />
                      <Text>AI powered</Text>
                      <Button
                        pale
                        onClick={() => setOpenGptModal(!openGptModal)}
                      >
                        Generate
                      </Button>
                    </ActionCard>
                  </Grid>
                }
              />
            )}
          </Box>
        </>
      )}
      {modal.show && (
        <QuestionEditor
          open={modal.show}
          closeModal={() => {
            setModal({
              show: false,
              data: null,
            });
          }}
          data={modal.data}
          currentPaper={paper}
          refetch={refetch}
        />
      )}

      {openBulkModal && (
        <BulkQuestionUpload
          open={openBulkModal}
          onClose={() => setOpenBulkModal(!openBulkModal)}
          paperId={paperId}
          refetch={refetch}
        />
      )}

      {openGptModal && (
        <GptEditor
          open={openGptModal}
          closeModal={() => setOpenGptModal(!openGptModal)}
          currentPaper={paper}
          refetch={refetch}
        />
      )}

      {deleteQuestionModal.openModal && (
        <DeleteModal
          isOpen={deleteQuestionModal.openModal}
          onClose={() =>
            setDeleteQuestionModal({ openModal: false, data: null })
          }
          title="Delete Question"
          description="The execution of this action will result in the removal of the question from the system."
          isLoading={loadingQuestions}
          onDelete={() => {
            handleDelete(deleteQuestionModal.data);
            setDeleteQuestionModal({ openModal: false, data: null });
          }}
        />
      )}
    </Box>
  );
};

export default Questions;

const QuestionCell = styled(Box)`
  background-color: #fff;
  border-radius: ${Theme.PrimaryRadius};
  box-sizing: border-box;
  padding: 22px;
  display: flex;
  flex-direction: column;
  max-width: 30vw;
  /* cursor: pointer; */
`;
const CardTop = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const CardBottom = styled.div`
  max-height: 100px;
`;

const ActionCard = styled.div`
  display: flex;
  font-size: 14px;
  padding: 15px 20px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  width: 143px;
  border-radius: 5px;
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.1);
`;
